import React, { useEffect, useState, useCallback, useRef } from "react";
import "./TimezoneSelect.scss";
import axios from "axios";
import Loader from "./Loader";

const SEARCH_DEBOUNCE_MS = 300;

function rankTimezone(timezone, query) {
    const label = (timezone.label || "").toLowerCase();
    const value = (timezone.value || "").toLowerCase();
    const q = query.toLowerCase();
    if (label === q || value === q) return 3;
    if (label.startsWith(q) || value.startsWith(q)) return 2;
    if (label.includes(q) || value.includes(q)) return 1;
    return 0;
}

const TIME_API_URL = process.env.REACT_APP_TIMEZONE_URL || "https://run.salesblink.io";

const COMMON_TIMEZONES = [
    { label: "UTC (UTC)", value: "UTC" },
    { label: "London (Europe/London)", value: "Europe/London" },
    { label: "Paris (Europe/Paris)", value: "Europe/Paris" },
    { label: "Berlin (Europe/Berlin)", value: "Europe/Berlin" },
    { label: "Moscow (Europe/Moscow)", value: "Europe/Moscow" },
    { label: "Dubai (Asia/Dubai)", value: "Asia/Dubai" },
    { label: "Mumbai (Asia/Kolkata)", value: "Asia/Kolkata" },
    { label: "Dhaka (Asia/Dhaka)", value: "Asia/Dhaka" },
    { label: "Bangkok (Asia/Bangkok)", value: "Asia/Bangkok" },
    { label: "Singapore (Asia/Singapore)", value: "Asia/Singapore" },
    { label: "Hong Kong (Asia/Hong_Kong)", value: "Asia/Hong_Kong" },
    { label: "Shanghai (Asia/Shanghai)", value: "Asia/Shanghai" },
    { label: "Tokyo (Asia/Tokyo)", value: "Asia/Tokyo" },
    { label: "Sydney (Australia/Sydney)", value: "Australia/Sydney" },
    { label: "New York (America/New_York)", value: "America/New_York" },
    { label: "Chicago (America/Chicago)", value: "America/Chicago" },
    { label: "Denver (America/Denver)", value: "America/Denver" },
    { label: "Los Angeles (America/Los_Angeles)", value: "America/Los_Angeles" },
    { label: "Toronto (America/Toronto)", value: "America/Toronto" },
    { label: "Sao Paulo (America/Sao_Paulo)", value: "America/Sao_Paulo" },
];

function getCurrentUsersTimezone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export default function TimezoneSelect({ autodetect, onChange, disabled, darkModeEnabled }) {
    const [timezonesData, setTimezonesData] = useState([]);
    const [selected, setSelected] = useState("");
    const [expanded, setExpanded] = useState(false);
    const [query, setQuery] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const abortControllerRef = useRef(null);
    const debounceRef = useRef(null);

    const fetchTimezones = useCallback(async (search) => {
        // Cancel any previous in-flight request so stale results can't overwrite the list.
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        const controller = new AbortController();
        abortControllerRef.current = controller;

        setIsLoading(true);
        setError(null);
        try {
            const result = await axios.get(
                `${TIME_API_URL}/api/public/timezone?search=${encodeURIComponent(search)}`,
                { signal: controller.signal }
            );
            const data = result?.data?.data || [];
            // Keep the order deterministic: best label/value match first, then alphabetical.
            const sorted = [...data].sort((a, b) => {
                const rankDiff = rankTimezone(b, search) - rankTimezone(a, search);
                if (rankDiff) return rankDiff;
                return (a.label || "").localeCompare(b.label || "");
            });
            // Ignore the result if a newer search has already started.
            if (controller.signal.aborted) return;
            setTimezonesData(sorted);
        } catch (err) {
            if (axios.isCancel?.(err) || err.name === "AbortError" || err.name === "CanceledError") {
                return;
            }
            if (controller.signal.aborted) return;
            setTimezonesData([]);
            setError("Could not load timezones. Please try again.");
        } finally {
            if (!controller.signal.aborted) {
                setIsLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        const detected = getCurrentUsersTimezone();
        setSelected(detected);
        if (onChange) onChange(detected);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        if (!query.trim()) {
            setTimezonesData(COMMON_TIMEZONES);
            setIsLoading(false);
            setError(null);
        } else {
            // Wait for the user to pause typing before hitting the API.
            debounceRef.current = setTimeout(() => {
                fetchTimezones(query);
            }, SEARCH_DEBOUNCE_MS);
        }

        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [query, fetchTimezones]);

    function handleSelect(timezone) {
        if (onChange) onChange(timezone.value);
        setSelected(timezone.value);
        setExpanded(false);
        setQuery("");
    }

    function toggleExpanded() {
        if (disabled) return;
        setExpanded(!expanded);
        if (!expanded) setQuery("");
    }

    const displayValue = String(selected).replace(/_/g, " ");

    return (
        <>
            {expanded && <div onClick={() => setExpanded(false)} className="selection-dismiss"></div>}
            <div className="timezone-select">
                <div
                    onClick={toggleExpanded}
                    className="selection-button"
                    style={{ cursor: disabled ? "default" : "pointer" }}
                    aria-expanded={expanded}
                    aria-haspopup="listbox"
                >
                    <TimezoneIcon darkModeEnabled={darkModeEnabled} />
                    <div className="selection-value">
                        {displayValue}
                        {!disabled && (
                            <svg width="20px" height="20px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <g strokeWidth="0"></g>
                                <g strokeLinecap="round" strokeLinejoin="round"></g>
                                <g>
                                    <path d="M7 10L12 15L17 10" stroke={darkModeEnabled ? "#ffffff" : "#3A3A3A"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
                                </g>
                            </svg>
                        )}
                    </div>
                </div>
                {expanded && (
                    <div className="selection-menu" role="listbox">
                        <input
                            onChange={(e) => setQuery(e.target.value)}
                            type="search"
                            className="selection-menu-search"
                            placeholder="Search Timezones"
                            value={query}
                            autoFocus
                        />
                        {isLoading ? (
                            <div className="selection-menu-loading">
                                <Loader style={{ width: 24, height: 24, borderWidth: 3 }} />
                            </div>
                        ) : error ? (
                            <div className="selection-menu-error">
                                <p>{error}</p>
                                <button onClick={() => fetchTimezones(query)}>Retry</button>
                            </div>
                        ) : (
                            <div className="selection-menu-items">
                                {timezonesData.length === 0 ? (
                                    <div className="selection-menu-empty">No timezones found</div>
                                ) : (
                                    timezonesData.map((timezone) => (
                                        <div
                                            key={timezone.value || timezone.label}
                                            onClick={() => handleSelect(timezone)}
                                            className={`selection-menu-item ${timezone.value === selected ? "selected" : ""}`}
                                            role="option"
                                            aria-selected={timezone.value === selected}
                                        >
                                            {timezone.label}
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    );
}

function TimezoneIcon({ darkModeEnabled }) {
    return (
        <svg width="30px" height="30px" viewBox="-0.5 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g strokeWidth="0"></g>
            <g strokeLinecap="round" strokeLinejoin="round"></g>
            <g>
                <path
                    d="M12 22.3201C17.5228 22.3201 22 17.8429 22 12.3201C22 6.79722 17.5228 2.32007 12 2.32007C6.47715 2.32007 2 6.79722 2 12.3201C2 17.8429 6.47715 22.3201 12 22.3201Z"
                    stroke={darkModeEnabled ? "#ffffff" : "#000000"}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                ></path>
                <path
                    d="M2 12.3201H22"
                    stroke={darkModeEnabled ? "#ffffff" : "#000000"}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                ></path>
                <path
                    d="M12 22.3201C13.933 22.3201 15.5 17.8429 15.5 12.3201C15.5 6.79722 13.933 2.32007 12 2.32007C10.067 2.32007 8.5 6.79722 8.5 12.3201C8.5 17.8429 10.067 22.3201 12 22.3201Z"
                    stroke={darkModeEnabled ? "#ffffff" : "#000000"}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                ></path>
            </g>
        </svg>
    );
}
